import { Body, Heading, Icon } from '@assembly-js/design-system';
import { t } from 'i18next';

import { PLAN_PAGE } from 'src/constants';
import useNavigate from 'src/hooks/useNavigate';
import { usePlanStatus } from 'src/hooks/usePlanStatusHook';
import { useRouteAccess } from 'src/routes/routeAccess';
import { getStudioPlanTitle } from 'src/routes/settings/plans/components/studio/studioFeatureGating';
import { SellableStudioPlanId } from 'src/routes/settings/plans/components/studio/studioPlanFeatures';

// Freemium's upgrade target is Pro (the design's "Upgrade to Pro").
const TARGET_PLAN: SellableStudioPlanId = 'professional';

/**
 * Sidebar CTA that sends a freemium workspace to the plans page. SidebarNav
 * renders it only once onboarding is complete; here it self-gates to freemium
 * Studio pricing workspaces — the only ones with a plan to upgrade to.
 *
 * It navigates rather than opening a modal: the plans page carries the usage
 * that tells you WHICH plan to move to, the credit options, and the enterprise
 * pitch, none of which fit in the modal.
 */
export function StudioUpgradePlanActionCard() {
  const { studio } = usePlanStatus();
  const { navigate } = useNavigate();
  const { routeAccess } = useRouteAccess();

  const isFreemiumStudioWorkspace =
    studio.isStudioPricingWorkspace &&
    !studio.isCurrentPlanLoading &&
    studio.studioPlan === 'freemium';

  // Hidden from internal users who cannot open the page it leads to, rather
  // than sending them into an access denial.
  if (!isFreemiumStudioWorkspace || !routeAccess.allowSettingsPlanPage) {
    return null;
  }

  return (
    <button
      type="button"
      className="mb-3 flex w-full appearance-none flex-row items-center gap-2 rounded border border-solid border-gray-150 bg-white py-1.5 pl-2 pr-3 shadow-none outline-hidden hover:cursor-pointer hover:bg-secondary-hover"
      onClick={() => navigate(PLAN_PAGE.path)}
    >
      {/* Light-teal tile + dark gem, matching the Figma sidebar CTA. */}
      <div className="flex shrink-0 items-center justify-center rounded bg-[#bce7f4] p-[9px]">
        <Icon icon="gem" className="h-3.5 w-3.5 text-black-heading" />
      </div>
      <div className="flex w-full flex-col items-start">
        <Heading size="xs" className="text-black-heading">
          {t(($) => $.page_studio_plans.sidebar_upgrade.title, {
            plan: getStudioPlanTitle(TARGET_PLAN),
          })}
        </Heading>
        <Body className="text-[10px] leading-4 text-secondary">
          {t(($) => $.page_studio_plans.sidebar_upgrade.subtitle)}
        </Body>
      </div>
    </button>
  );
}
